/**
 * The booking column's standard look (the prototype's .o1Box): the price and
 * terms, the month calendar and the chosen day's times (an event: its one date
 * and time), the pick, and Continue. Draws the behavior half's state and calls
 * its actions; holds no rule. Draws nothing until the offering has loaded.
 */
import { LOCATION_KIND_LABEL } from '@telify/shared';
import { partsOf, pieceRoot } from '../../contract/runtime';
import { formatDayLabel, formatDuration, formatSlotTime, priceText, slotTimeLabels } from '../../format';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { nextMonth, type BookingColumnRuntime } from './useBookingColumn';
import css from './BookingColumn.module.css';
import { contract } from './contract';
import { termsText } from './terms';

const part = partsOf(contract);

const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/** A calendar day (YYYY-MM-DD) in words: "Friday, October 16". */
function dayInWords(ymd: string): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${ymd}T12:00:00Z`),
  );
}

function previousMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

export function BookingColumnView({ state, actions, options }: BookingColumnRuntime) {
  const offering = state.kind === 'service' ? state.service : state.event;
  if (!offering) return null;
  const { service, event } = state;
  const approval = Boolean(service?.requiresApproval);
  const terms = termsText(offering.payment, offering.price, approval);
  const label = options.continueLabel || 'Continue';
  const name = service ? service.name : event!.title;
  const labels = slotTimeLabels(state.times.map((t) => t.startsAt), state.timezone);
  const pickedAt = state.times.findIndex((t) => t.startsAt === state.selectedStart);
  const when = state.selectedStart
    ? `${formatDayLabel(state.selectedStart, state.timezone)} · ${labels[pickedAt] ?? formatSlotTime(state.selectedStart, state.timezone)}`
    : null;

  return (
    <aside className={css.box} aria-label={`Book ${name}`} {...pieceRoot(contract)} {...part('column')}>
      <div className={css.price}>
        <b {...part('price')}>{priceText(offering.price)}</b>
        <span {...part('format')}>
          {service ? `${formatDuration(service.durationMinutes)} · ` : 'Event · '}
          {LOCATION_KIND_LABEL[offering.modality]}
        </span>
      </div>
      {terms && (
        <div className={css.terms} {...part('terms')}>
          <Icon name="card" small />
          <span>{terms}</span>
        </div>
      )}
      {approval && (
        <div className={css.terms} {...part('approval')}>
          <Icon name="clock" small />
          <span>Requires approval</span>
        </div>
      )}
      {event && !state.eventClosed && !state.booked && (
        <div className={`${css.terms} ${event.spotsLeft <= 3 ? css.low : ''}`} {...part('spots', event.spotsLeft <= 3 && 'low')}>
          <Icon name="users" small />
          {event.spotsLeft === 1 ? '1 spot left' : `${event.spotsLeft} spots left`}
        </div>
      )}

      {state.paused ? (
        <p className={css.stop} {...part('paused')}>
          New bookings are paused for now.
        </p>
      ) : event ? (
        <EventTime state={state} />
      ) : (
        <ServicePicker state={state} actions={actions} options={options} />
      )}

      {state.eventClosed && !state.booked && (
        <p className={css.stop} {...part('closed')}>
          {state.eventClosed === 'full' ? 'This event is full.' : 'Registration for this event is closed.'}
        </p>
      )}

      {state.booked && (
        <button type="button" className={`${shared.btn} ${shared.btnPrimary} ${css.cta}`} onClick={actions.viewBooking} {...part('view-booking')}>
          View booking
          <Icon name="arrow" />
        </button>
      )}

      {!state.paused && !state.eventClosed && !state.booked && (
        <>
          {service && !when && (
            <p className={css.pick} {...part('pick-note')}>
              Choose a time to continue.
            </p>
          )}
          {when && (
            <dl className={css.sum} {...part('summary')}>
              <div>
                <dt>Date and time</dt>
                <dd>{when}</dd>
              </div>
              {offering.price.kind !== 'FROM' && (
                <div className={css.tot}>
                  <dt>Total</dt>
                  <dd>{priceText(offering.price)}</dd>
                </div>
              )}
            </dl>
          )}
          <button
            type="button"
            className={`${shared.btn} ${shared.btnPrimary} ${css.cta}`}
            disabled={!state.canContinue}
            onClick={actions.continue}
            {...part('continue', !state.canContinue && 'disabled')}
          >
            {label}
            <Icon name="arrow" />
          </button>
          {!state.signedIn && (
            <p className={css.note} {...part('note')}>
              {event
                ? 'Your spot is held once you sign in with Telegram to confirm.'
                : approval
                  ? 'The time is held once you sign in with Telegram to send the request.'
                  : 'The time is held once you sign in with Telegram to confirm.'}
            </p>
          )}
        </>
      )}
    </aside>
  );
}

function EventTime({ state }: Pick<BookingColumnRuntime, 'state'>) {
  if (!state.selectedDay || !state.selectedStart) return null;
  const time = formatSlotTime(state.selectedStart, state.timezone);
  return (
    <div className={css.evFixed} {...part('event-time')}>
      <div className={css.dayLabel}>
        <b {...part('day-label')}>{dayInWords(state.selectedDay)}</b>
        <span {...part('zone')}>{state.zoneName}</span>
      </div>
      <div className={css.times} {...part('times')}>
        <span className={`${css.time} ${css.picked} ${css.fixed}`} aria-label={`Starts at ${time}`} {...part('time', 'selected')}>
          {time}
        </span>
      </div>
    </div>
  );
}

function ServicePicker({ state, actions, options }: BookingColumnRuntime) {
  const labels = slotTimeLabels(state.times.map((t) => t.startsAt), state.timezone);
  const [y, m] = state.month.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const length = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const monthName = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(first);
  const open = new Set(state.openDays);
  const days = Array.from({ length }, (_, i) => `${state.month}-${String(i + 1).padStart(2, '0')}`);

  return (
    <>
      <div className={css.cal} {...part('calendar')}>
        <div className={css.calHead}>
          <div className={css.calMonth} aria-live="polite" {...part('month')}>
            {monthName}
          </div>
          <div className={css.calNav}>
            <button
              type="button"
              aria-label="Previous month"
              disabled={state.month <= state.today.slice(0, 7)}
              onClick={() => actions.showMonth(previousMonth(state.month))}
              {...part('month-prev', state.month <= state.today.slice(0, 7) && 'disabled')}
            >
              <Icon name="left" small />
            </button>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => actions.showMonth(nextMonth(state.month))}
              {...part('month-next')}
            >
              <Icon name="right" small />
            </button>
          </div>
        </div>
        <div className={css.calGrid}>
          {WEEKDAYS.map((w) => (
            <span key={w} className={css.calWk} aria-hidden="true" {...part('weekday')}>
              {w}
            </span>
          ))}
          {Array.from({ length: first.getUTCDay() }, (_, i) => (
            <span key={`blank-${i}`} aria-hidden="true" />
          ))}
          {days.map((day, i) => {
            const isOpen = open.has(day);
            const cls = [
              css.calDay,
              isOpen ? css.open : '',
              day === state.selectedDay ? css.sel : '',
              day === state.today ? css.today : '',
            ].join(' ');
            return (
              <button
                key={day}
                type="button"
                className={cls}
                disabled={!isOpen}
                aria-pressed={isOpen ? day === state.selectedDay : undefined}
                aria-label={isOpen ? dayInWords(day) : `${dayInWords(day)}, not available`}
                onClick={() => actions.pickDay(day)}
                {...part('day', !isOpen ? 'disabled' : day === state.selectedDay ? 'selected' : 'open')}
              >
                <span>{i + 1}</span>
              </button>
            );
          })}
        </div>
      </div>

      {state.status === 'error' ? (
        <p className={css.stop} {...part('no-times')}>
          Open times could not load. Please try again later.
        </p>
      ) : (
        state.status === 'ready' &&
        state.openDays.length === 0 &&
        !state.selectedDay && (
          <p className={css.pick} {...part('no-times')}>
            No open times this month.
          </p>
        )
      )}

      {state.selectedDay && (
        <>
          <div className={css.dayLabel}>
            <b {...part('day-label')}>{dayInWords(state.selectedDay)}</b>
            {options.showZone && <span {...part('zone')}>{state.zoneName}</span>}
          </div>
          <div className={css.times} {...part('times')}>
            {state.timesStatus === 'ready' && state.times.length === 0 && (
              <p className={css.noTimes} {...part('no-times')}>
                None available
              </p>
            )}
            {state.timesStatus === 'error' && (
              <p className={css.noTimes} {...part('no-times')}>
                Open times could not load. Please try again later.
              </p>
            )}
            {state.times.map((t, i) => {
              const picked = t.startsAt === state.selectedStart;
              return (
                <button
                  key={t.startsAt}
                  type="button"
                  className={`${css.time} ${picked ? css.picked : ''}`}
                  aria-pressed={picked}
                  onClick={() => actions.pickTime(t.startsAt)}
                  {...part('time', picked && 'selected')}
                >
                  {labels[i]}
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
